import { useState } from "react";
import type { CandidateProfile } from "@applicant-reviewer/api";
import { useUpdateProfile } from "../../../../hooks/mutations/useProfileMutations";

interface PersonalInfoSectionProps {
  profile: CandidateProfile;
}

interface FormState {
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

function profileToForm(profile: CandidateProfile): FormState {
  return {
    firstName: profile.firstName ?? "",
    lastName: profile.lastName ?? "",
    phone: profile.phone ?? "",
    address: profile.address ?? "",
    city: profile.city ?? "",
    state: profile.state ?? "",
    zip: profile.zip ?? "",
  };
}

export default function PersonalInfoSection({ profile }: PersonalInfoSectionProps) {
  const [form, setForm] = useState<FormState>(() => profileToForm(profile));
  const [saved, setSaved] = useState(false);
  const updateProfile = useUpdateProfile();

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setSaved(false);
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = () => {
    updateProfile.mutate(
      {
        firstName: form.firstName || undefined,
        lastName: form.lastName || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        zip: form.zip || undefined,
      },
      {
        onSuccess: () => setSaved(true),
      },
    );
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0156FF] focus:border-transparent transition";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
      <h2 className="text-base font-bold text-gray-900">Personal Information</h2>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">First name</label>
          <input
            type="text"
            value={form.firstName}
            onChange={handleChange("firstName")}
            placeholder="Jane"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Last name</label>
          <input
            type="text"
            value={form.lastName}
            onChange={handleChange("lastName")}
            placeholder="Smith"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Phone</label>
        <input
          type="tel"
          value={form.phone}
          onChange={handleChange("phone")}
          placeholder="(555) 000-0000"
          className={inputClass}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
        <input
          type="email"
          value={profile.email}
          readOnly
          className={`${inputClass} bg-gray-50 text-gray-400 cursor-not-allowed`}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Address</label>
        <input
          type="text"
          value={form.address}
          onChange={handleChange("address")}
          placeholder="123 Main St"
          className={inputClass}
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1">
          <label className="block text-xs font-semibold text-gray-600 mb-1">City</label>
          <input
            type="text"
            value={form.city}
            onChange={handleChange("city")}
            placeholder="Seattle"
            className={inputClass}
          />
        </div>
        <div className="col-span-1">
          <label className="block text-xs font-semibold text-gray-600 mb-1">State</label>
          <input
            type="text"
            value={form.state}
            onChange={handleChange("state")}
            placeholder="WA"
            maxLength={2}
            className={inputClass}
          />
        </div>
        <div className="col-span-1">
          <label className="block text-xs font-semibold text-gray-600 mb-1">ZIP</label>
          <input
            type="text"
            value={form.zip}
            onChange={handleChange("zip")}
            placeholder="98101"
            maxLength={10}
            className={inputClass}
          />
        </div>
      </div>

      {updateProfile.isError && (
        <p className="text-xs text-red-600">Save failed. Please try again.</p>
      )}

      {saved && !updateProfile.isPending && (
        <p className="text-xs text-green-600 font-semibold">Saved!</p>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={updateProfile.isPending}
        className="w-full h-12 rounded-full bg-[#0156FF] text-white text-sm font-bold hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {updateProfile.isPending ? "Saving..." : "Save"}
      </button>
    </div>
  );
}
